/**
 * PATH: packages/ui/src/speech/ensureMicPermission.ts
 * FILE_ID: UI-SPEECH-MIC-PERM-001
 * PURPOSE: Ensure microphone permission before getUserMedia / Web Speech.
 * WHY: Capacitor WebViews need an explicit permission dance on iOS/Android;
 *      browsers often prompt on getUserMedia alone. Keep zero hard dependency
 *      on @capacitor/core (hosted SPA reads window.Capacitor when present).
 * FLOW: Capacitor plugin requestPermissions? → Permissions API → 'prompt'
 */

export type MicPermissionState = 'granted' | 'denied' | 'prompt'

type CapacitorPlugin = {
  requestPermissions?: () => Promise<{ microphone?: string; speechRecognition?: string }>
  checkPermissions?: () => Promise<{ microphone?: string; speechRecognition?: string }>
}

function capacitorPlugins(): Record<string, CapacitorPlugin> | null {
  if (typeof window === 'undefined') return null
  const cap = (window as unknown as {
    Capacitor?: { isNativePlatform?: () => boolean; Plugins?: Record<string, CapacitorPlugin> }
  }).Capacitor
  try {
    if (!cap?.isNativePlatform?.()) return null
  } catch {
    return null
  }
  return cap.Plugins ?? null
}

function mapState(raw: string | undefined): MicPermissionState | null {
  if (!raw) return null
  const v = raw.toLowerCase()
  if (v === 'granted' || v === 'authorized') return 'granted'
  if (v === 'denied' || v === 'restricted') return 'denied'
  if (v === 'prompt' || v === 'prompt-with-rationale') return 'prompt'
  return null
}

/**
 * Best-effort: request mic access on native shells, then query Permissions API.
 * Callers still run getUserMedia — that remains the authoritative grant path.
 */
export async function ensureMicPermission(): Promise<MicPermissionState> {
  const plugins = capacitorPlugins()
  if (plugins) {
    // Prefer a dedicated Microphone plugin when the native shell ships one;
    // SpeechRecognition is a common Capacitor community stand-in that still
    // only requests the OS mic entitlement (we do not use its STT).
    const candidate =
      plugins.Microphone ||
      plugins.SpeechRecognition ||
      plugins.Permissions
    try {
      if (candidate?.checkPermissions) {
        const cur = await candidate.checkPermissions()
        const mapped = mapState(cur.microphone || cur.speechRecognition)
        if (mapped === 'granted' || mapped === 'denied') return mapped
      }
      if (candidate?.requestPermissions) {
        const next = await candidate.requestPermissions()
        const mapped = mapState(next.microphone || next.speechRecognition)
        if (mapped) return mapped
      }
    } catch {
      /* fall through to Permissions API / getUserMedia */
    }
  }

  try {
    const perms = navigator.permissions
    if (perms?.query) {
      const status = await perms.query({
        name: 'microphone' as PermissionName,
      })
      return mapState(status.state) ?? 'prompt'
    }
  } catch {
    /* Firefox / older WebViews reject microphone PermissionName */
  }
  return 'prompt'
}
